import { CylinderGeometry, Mesh, Object3D, Raycaster, SphereGeometry, Vector3 } from "three"
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils'
import C from "../lib/Constants"

export default class Trees
{
    constructor(_options)
    {
        this.materials = _options.materials
        this.layout = _options.layout

        this.scatterObjects = this.layout.instance.children
        this.terrain = this.scatterObjects.find(x => { return x.name === 'map' })
        this.forest = this.layout.forest
        this.buildingBase = this.layout.buildingBase
        this.treeBase = this.layout.treeBase

        this.container = new Object3D()
        this.container.name = 'trees_container'

        this.raycaster = new Raycaster()
        this.treeBaseGeometry = this.createTreeGeometry()
        this.bushBaseGeometry = this.createBushGeometry()
    }

    static async init(_options)
    {
        const startTime = performance.now()
        const trees = new Trees(_options)

        // Add woods
        let treesGeometries = trees.scatterVeg(
        {
            nTrees: C.TREES.nOfTreesInWoods,
            nBushes: 0,
            baseGeometry: trees.treeBase,
            terrainTypes: [C.TERRAIN_TYPES.TreeBase]
        })
        // Add trees and bushes scattered around
        let scatteredGeometries = trees.scatterVeg(
        {
            nTrees: C.TREES.nOfTreesScattered,
            nBushes: C.TREES.nOfBushesScattered,
            baseGeometry: trees.terrain,
            terrainTypes: [C.TERRAIN_TYPES.Map]
        })
        treesGeometries = treesGeometries.concat(scatteredGeometries)
        // Add park
        let forestGeometries = trees.scatterVeg(
        {
            nTrees: C.TREES.nOfTreesInPark,
            nBushes: 0,
            baseGeometry: trees.forest,
            terrainTypes: [C.TERRAIN_TYPES.Forest]
        })

        // Create meshes
        trees.mat = trees.materials.items.treesMat

        trees.treesMergedGeo = mergeGeometries(treesGeometries)
        trees.treesMergedGeo.computeVertexNormals()
        trees.treesMergedGeo.computeTangents()
        trees.treesMesh = new Mesh(trees.treesMergedGeo, trees.mat)
        trees.treesMesh.castShadow = true
        trees.treesMesh.receiveShadow = true
        trees.treesMesh.matrixAutoUpdate = false
        trees.container.add(trees.treesMesh)

        trees.forestGeo = mergeGeometries(forestGeometries)
        trees.forestGeo.computeVertexNormals()
        trees.forestGeo.computeTangents()
        trees.forestMesh = new Mesh(trees.forestGeo, trees.mat)
        trees.forestMesh.name = "forest"
        trees.forestMesh.castShadow = true
        trees.forestMesh.receiveShadow = true
        trees.forestMesh.matrixAutoUpdate = false
        trees.container.add(trees.forestMesh)

        const elapsed = ((performance.now() - startTime) / 1000).toPrecision(4)

        return trees
    }

    scatterVeg(params)
    {
        const geoArray = []

        let createdTrees = 0
        let createdBushes = 0

        // Calc terrain size
        params.xSize = Math.abs(params.baseGeometry.geometry.boundingBox.max.x - params.baseGeometry.geometry.boundingBox.min.x)
        params.zSize = Math.abs(params.baseGeometry.geometry.boundingBox.max.z - params.baseGeometry.geometry.boundingBox.min.z)

        // Add intersectable objects
        params.intersectObjects = [params.baseGeometry]
        if(params.baseGeometry.name !== 'map') params.intersectObjects.push(this.terrain)

        // Create trees
        while(createdTrees < params.nTrees)
        {
            params.isTree = true
            const tree = this.placeVeg(params)
            if(tree !== undefined)
            {
                geoArray.push(tree)
                createdTrees++
            }
        }

        // Create bushes
        while(createdBushes < params.nBushes)
        {
            params.isTree = false
            const bush = this.placeVeg(params)
            if(bush !== undefined)
            {
                geoArray.push(bush)
                createdBushes++
            }
        }

        return geoArray
    }

    placeVeg(params)
    {
         // Point to random position
        let xPos = (Math.random() * params.xSize) + params.baseGeometry.geometry.boundingBox.min.x
        let zPos = (Math.random() * params.zSize) + params.baseGeometry.geometry.boundingBox.min.z

        // Set raycaster
        const origin = new Vector3(xPos, 99, zPos)
        const direction = new Vector3(0, -1, 0)
        this.raycaster.set(origin, direction)

        // Intersect
        const intersects = this.raycaster.intersectObjects(params.intersectObjects, false)

        // Discard missed ray
        const intersectType = this.getIntersectionType(intersects)
        if(!params.terrainTypes.includes(intersectType)) return
        if(intersectType === C.TERRAIN_TYPES.Lot) return
        if(intersectType === C.TERRAIN_TYPES.BuildingBase) return

        // Get terrain height
        const map = intersects.find(x => x.object.name === "map")
        if(map === undefined) return
        const terrainY = map.point.y

        // Create geometry
        const veg = params.isTree ? this.treeBaseGeometry.clone() : this.bushBaseGeometry.clone()
        const scaleFactor = (Math.random() - 0.5) * (params.isTree ? C.TREES.treeScaleRandomRange : C.TREES.bushScaleRandomRange)
        const scale = (params.isTree ? C.TREES.treeBaseScale : C.TREES.bushBaseScale) + (scaleFactor * (params.isTree ? C.TREES.treeBaseScale : C.TREES.bushBaseScale))
        veg.scale(scale, scale, scale)
        veg.translate(xPos, terrainY, zPos)

        return veg
    }

    createTreeGeometry()
    {
        // Create spherical foliage
        const foliage = new SphereGeometry(1, 8, 4)
        foliage.translate(0, C.TREES.treeBaseHeight, 0)

        // Create cylindrical trunk
        const trunkSize = 0.15
        const trunk = new CylinderGeometry(trunkSize, trunkSize, C.TREES.treeBaseHeight * 1.25, 6, 1, true)
        trunk.translate(0, C.TREES.treeBaseHeight * 0.5, 0)

        // Create Geometry
        const tree = mergeGeometries([foliage, trunk])
        return tree
    }

    createBushGeometry()
    {
        // Create geometry
        const bush = new SphereGeometry(1, 8, 4, 0, Math.PI)
        bush.rotateX(- Math.PI * 0.5)
        bush.rotateY = Math.random()
        return bush
    }

    getIntersectionType(intersects)
    {
        if(!intersects || intersects.length < 1)                                return C.TERRAIN_TYPES.None
        else if(intersects.some(x => x.object.name === 'tree_base'))            return C.TERRAIN_TYPES.TreeBase
        else if(intersects.length === 1 && intersects[0].object.name === "map") return C.TERRAIN_TYPES.Map
        else if(intersects.some(x => x.object.name === "forest"))               return C.TERRAIN_TYPES.Forest
        else if(intersects.some(x => x.object.name === "building_base"))        return C.TERRAIN_TYPES.BuildingBase
        else                                                                    return C.TERRAIN_TYPES.Lot
    }
}